import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import * as sdkTypes from '@vansite/ts-sharetribe-flex-sdk/types';
import React, { memo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import Link from '../../components/Links/Link/Link';
import config from '../../config';
import routeConfiguration from '../../routeConfiguration';
import { formatMoney } from '../../util/currency';
import useAppSelector from '../../util/hooks/useAppSelector';
import useScreenSize from '../../util/hooks/useScreenSize';
import { createResourceLocatorString } from '../../util/routes';
import {
  createSubscriptionCheckoutSession,
  fetchSubscriptionProducts,
} from '../PassSettingsPage/PassSettingsPage.duck';
import ListingSection from './ListingSection';
import ListingSectionItem from './ListingSectionItem';

const { Money } = sdkTypes;

// The message rendered below embeds raw HTML. ICU reads those tags as tag nodes,
// so every tag is apostrophe-quoted in the catalogs ('<strong>'…'</strong>') to
// keep each message a single literal node — a tag node formatted without tag
// values throws once the catalogs ship as AST, making react-intl render the bare
// message id.
//
// The quoting only disappears if the ICU parser actually runs. `formatMessage`
// short-circuits on a "hot path" that returns the catalog string verbatim when
// no values argument is passed (see @formatjs/intl `formatMessage`), which would
// leak the apostrophes into the rendered HTML. Passing an (empty) values object
// forces the parser to run, so the message resolves to a single literal node and
// the HTML comes out exactly as authored.
const NO_VALUES = {};

/**
 * @param {Object} props
 * @param {import('@vansite/ts-sharetribe-flex-sdk').Money} props.price - The price of the listing
 */
const SectionAnnualPassSavingsMaybeComponent = ({ price }) => {
  const intl = useIntl();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { isMobile } = useScreenSize();
  const currentUser = useAppSelector((state) => state.user.currentUser);

  const [loading, setLoading] = useState(false);

  if (!price) return null;

  const isPlusListing = price.amount >= config.annualPass.lightPriceThreshold;
  const pricePlusCustomerCommission = Math.round(price.amount * 1.3);
  const reducedAmount = Math.round(pricePlusCustomerCommission * (isPlusListing ? 0.5 : 1));
  const reducedPrice = formatMoney(intl, new Money(reducedAmount, price.currency));

  const perNight = intl.formatMessage({ id: 'ListingPage.perNight' });
  const reducedPriceLabel = (
    <Box component="span" sx={{
      textDecoration: 'underline',
      textDecorationColor: theme.palette.success.main,
    }}>
      <FormattedMessage id="ListingPage.annualPassSavings.reducedPrice" />:
      {` ${reducedPrice} ${perNight}`}
    </Box>
  );

  const handleSubscriptionCheckoutSession = () => {
    setLoading(true);
    (async () => {
      const subscriptionProducts = await dispatch(fetchSubscriptionProducts(intl.locale));
      const annualPassProduct = (subscriptionProducts || []).find(
        (p) => p.metadata.guestCount === '2' && p.metadata.twentyFivePass !== 'true',
      );
      if (!annualPassProduct?.default_price?.id) {
        setLoading(false);
        return;
      }
      window.location.href = await dispatch(createSubscriptionCheckoutSession({
        priceId: annualPassProduct.default_price.id,
        intl,
      }));
    })();
  };

  return (
    <ListingSection>
      <ListingSectionItem
        params={{
          sx: { justifyContent: { xs: 'center', md: 'flex-start' } },
        }}
      >
        <Typography component="h3" mb={2} variant="h4">
          <FormattedMessage
            id={`ListingPage.annualPass${isPlusListing ? 'Plus' : ''}Savings.title`}
            values={{ reducedPriceLabel }}
          />
        </Typography>
      </ListingSectionItem>
      <ListingSectionItem>
        <Stack spacing={2}>
          <Typography
            dangerouslySetInnerHTML={{ __html: intl.formatMessage({ id: 'ListingPage.annualPassSavings.Text' }, NO_VALUES) }}
            sx={{ whiteSpace: 'pre-line', textAlign: 'left' }}
            variant="body1"
          />
          <Stack direction="row" spacing={2}>
            {currentUser ? (
              <Button
                color="primary"
                fullWidth={isMobile}
                loading={loading}
                onClick={handleSubscriptionCheckoutSession}
                size={isMobile ? 'small' : 'medium'}
                variant="contained"
              >
                <FormattedMessage id="ListingPage.annualPassSavings.buycta" />
              </Button>
            ) : (
              <Button
                color="primary"
                component={Link}
                fullWidth={isMobile}
                size={isMobile ? 'small' : 'medium'}
                target={isMobile ? '_self' : '_blank'}
                to="https://shop.vansite.eu/products/jahrespass"
                variant="contained"
              >
                <FormattedMessage id="ListingPage.annualPassSavings.buycta" />
              </Button>
            )}
            {currentUser ? (
              <Button
                color="primary"
                component={Link}
                fullWidth={isMobile}
                size={isMobile ? 'small' : 'medium'}
                to={createResourceLocatorString(
                  'PassSettingsPage',
                  routeConfiguration(),
                  {},
                  {},
                  intl.locale,
                )}
                variant="outlined"
              >
                <FormattedMessage id="ListingPage.annualPassSavings.cta" />
              </Button>
            ) : (
              <Button
                color="primary"
                component={Link}
                fullWidth={isMobile}
                size={isMobile ? 'small' : 'medium'}
                target={isMobile ? '_self' : '_blank'}
                to={createResourceLocatorString(
                  'StoryblokPage',
                  routeConfiguration(),
                  { slug: 'pass' },
                  {},
                  intl.locale,
                )}
                variant="outlined"
              >
                <FormattedMessage id="ListingPage.annualPassSavings.cta" />
              </Button>
            )}
          </Stack>
        </Stack>
      </ListingSectionItem>
    </ListingSection>
  );
};

SectionAnnualPassSavingsMaybeComponent.displayName = 'SectionAnnualPassSavingsMaybe';

const SectionAnnualPassSavingsMaybe = memo(SectionAnnualPassSavingsMaybeComponent);

export default SectionAnnualPassSavingsMaybe;
